import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import '../styles/corporate.css';
import { 
  Shield, 
  AlertTriangle, 
  FileCheck, 
  Clock, 
  CheckCircle2, 
  X,
  ArrowRight,
  Users,
  Bell,
  FileText,
  BarChart3,
  Menu,
  XIcon,
  Mail,
  Phone,
  Building2,
  Send,
  Target,
  Lightbulb,
  TrendingUp,
  Sparkles
} from 'lucide-react';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitMessage('Merci ! Votre demande a été envoyée avec succès. Nous vous recontacterons sous 24h.');
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
      } else {
        setSubmitMessage('Une erreur est survenue. Veuillez réessayer.');
      }
    } catch (error) {
      setSubmitMessage('Erreur de connexion. Veuillez réessayer plus tard.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const navItems = [
    { label: 'Accueil', id: 'accueil' },
    { label: 'À propos', id: 'apropos' },
    { label: 'Offre de services', id: 'services' },
    { label: 'FAQ', id: 'faq' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div 
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => scrollToSection('accueil')}
            >
              <img 
                src="/assets/secura-logo.png" 
                alt="SECURA Consulting" 
                className="h-24 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-gray-700 hover:text-blue-900 font-medium transition-colors duration-200"
                >
                  {item.label}
                </button>
              ))}
              <Button 
                onClick={() => scrollToSection('contact')}
                className="btn-corporate"
              >
                Demander un audit
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isMenuOpen ? <XIcon className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="lg:hidden mt-4 pb-4 flex flex-col gap-3 border-t pt-4">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-gray-700 hover:text-blue-900 font-medium py-2 px-4 rounded-lg hover:bg-gray-50 transition-all"
                >
                  {item.label}
                </button>
              ))}
              <Button 
                onClick={() => scrollToSection('contact')}
                className="btn-corporate"
              >
                Demander un audit
              </Button>
            </nav>
          )}
        </div>
      </header>

      {/* SECTION ACCUEIL */}
      <section id="accueil" className="pt-24 scroll-mt-20">
        {/* Hero Section */}
        <div className="corporate-section bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
          {/* Subtle background image */}
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
            <img 
              src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1920&q=80" 
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              
              <h1 className="corporate-heading text-5xl md:text-6xl lg:text-7xl mb-6">
                La cybersécurité des PME et ETI, sans compromis.
              </h1>
              
              <p className="corporate-subheading text-xl md:text-2xl mb-10 max-w-3xl mx-auto">
                Secura Consulting vous accompagne de l'obligation cyber à la maîtrise — de manière <span className="text-blue-900 font-semibold">pragmatique, sans jargon inutile.</span>
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
                <Button 
                  size="lg" 
                  onClick={() => scrollToSection('contact')}
                  className="btn-corporate text-lg px-8 py-6"
                >
                  Prendre RDV
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  onClick={() => scrollToSection('services')}
                  className="btn-corporate-outline text-lg px-8 py-6"
                >
                  Découvrir notre approche
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Bande de Réassurance */}
        <div className="bg-blue-900 text-white py-8">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="flex items-center justify-center gap-3 text-center md:text-left">
                <Shield className="h-8 w-8 flex-shrink-0" />
                <span className="text-base font-medium">Approche pragmatique & sur-mesure</span>
              </div>
              <div className="flex items-center justify-center gap-3 text-center md:text-left">
                <BarChart3 className="h-8 w-8 flex-shrink-0" />
                <span className="text-base font-medium">Solution AI Risk Assessment</span>
              </div>
              <div className="flex items-center justify-center gap-3 text-center md:text-left">
                <Target className="h-8 w-8 flex-shrink-0" />
                <span className="text-base font-medium">Spécialiste PME & ETI</span>
              </div>
            </div>
          </div>
        </div>

        {/* Problem Statement */}
        <div className="corporate-section-alt">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center mb-12">
              
              <h2 className="corporate-heading text-4xl md:text-5xl mb-4">
                La plupart des entreprises ne sont pas prêtes.
              </h2>
              <p className="text-xl text-gray-600">Et elles ne le savent pas encore.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {[
                "Vous ne savez pas si votre entreprise est concernée par NIS2.",
                "Aucun inventaire de vos actifs critiques n'est formalisé.",
                "Vos politiques de sécurité existent… mais ne sont ni testées ni documentées.",
                "En cas d'incident, vous ne pouvez pas prouver que vous avez agi."
              ].map((item, index) => (
                <div 
                  key={index}
                  className="corporate-card flex items-start gap-4 hover:shadow-lg transition-shadow"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-red-500" />
                  </div>
                  <p className="text-gray-700 pt-2 leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* NIS2 Requirements */}
        <div className="corporate-section">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center mb-12">
              
              <h2 className="corporate-heading text-4xl md:text-5xl mb-4">
                Ce que NIS2 exige réellement
              </h2>
              <p className="text-xl text-gray-600">
                Une directive qui transforme les obligations de cybersécurité en responsabilités de gouvernance.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {[
                {
                  icon: Users,
                  title: "Responsabilité des dirigeants",
                  description: "NIS2 engage personnellement la direction. Les sanctions visent les décideurs, pas seulement l'entreprise."
                },
                {
                  icon: FileText,
                  title: "Obligation de preuve",
                  description: "Il ne suffit plus d'agir : il faut documenter, tracer et pouvoir démontrer chaque mesure prise."
                },
                {
                  icon: Bell,
                  title: "Notification d'incidents",
                  description: "24h pour notifier un incident significatif. Sans processus en place, c'est un risque de non-conformité immédiat."
                },
                {
                  icon: BarChart3,
                  title: "Auditabilité permanente",
                  description: "Les autorités peuvent exiger un audit à tout moment. Votre système de conformité doit être prêt en continu."
                }
              ].map((item, index) => (
                <Card key={index} className="corporate-card">
                  <CardHeader className="pb-3">
                    <div className="w-14 h-14 rounded-lg bg-blue-600 flex items-center justify-center mb-4">
                      <item.icon className="h-7 w-7 text-white" />
                    </div>
                    <CardTitle className="text-xl text-blue-900">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION À PROPOS */}
      <section id="apropos" className="scroll-mt-20">
        {/* Why Classic Approaches Fail */}
        <div className="corporate-section-alt relative overflow-hidden">
          {/* Subtle background image - better blended */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
            <img 
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80" 
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center mb-12">
              
              <h2 className="corporate-heading text-4xl md:text-5xl mb-4">
                Pourquoi les approches classiques échouent
              </h2>
              <p className="text-xl text-gray-600">
                NIS2 ne demande ni un rapport, ni un outil. Elle exige un système de conformité opérationnel.
              </p>
            </div>

            {/* Professional Image */}
            <div className="max-w-4xl mx-auto mb-12">
              <img 
                src="https://images.unsplash.com/photo-1523875194681-bedd468c58bf?w=1200&q=85" 
                alt="Stratégie et complexité business"
                className="w-full h-80 object-cover rounded-2xl shadow-lg"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {[
                {
                  title: "Cabinets de conseil",
                  items: ["Rapports théoriques", "Peu d'opérationnel", "Coûts élevés, livrables abstraits"],
                  highlight: false
                },
                {
                  title: "Acteurs cybersécurité",
                  items: ["Focus technique", "Pas de vision conformité", "Jargon incompréhensible pour la direction"],
                  highlight: false
                },
                {
                  title: "Outils SaaS",
                  items: ["Checklists génériques", "Aucune contextualisation", "Faux sentiment de conformité"],
                  highlight: false
                },
                {
                  title: "Notre approche",
                  items: ["Un système de conformité opérationnel, déployé dans votre organisation, avec des livrables concrets, une gouvernance claire et une traçabilité complète — prêt pour l'audit dès le premier jour."],
                  highlight: true
                }
              ].map((column, index) => (
                <Card 
                  key={index} 
                  className={`transition-all ${
                    column.highlight 
                      ? 'bg-blue-900 text-white border-blue-900 shadow-lg' 
                      : 'corporate-card'
                  }`}
                >
                  <CardHeader>
                    <CardTitle className={`text-lg ${column.highlight ? 'text-white' : 'text-blue-900'}`}>
                      {column.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {column.items.map((item, i) => (
                        <li key={i} className={`text-sm flex items-start gap-2 ${column.highlight ? 'text-gray-100' : 'text-gray-600'}`}>
                          {column.highlight ? (
                            <CheckCircle2 className="h-5 w-5 text-blue-300 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                          )}
                          <span className="leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* What Distinguishes Us */}
        <div className="corporate-section">
          <div className="container mx-auto px-6">
            <h2 className="corporate-heading text-4xl md:text-5xl mb-12 text-center">
              Ce qui nous distingue
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
              <Card className="border-l-4 border-l-red-500">
                <CardHeader>
                  <CardTitle className="text-2xl text-red-600 flex items-center gap-2">
                    <X className="h-6 w-6" />
                    CE QUE VOUS N'OBTIENDREZ PAS
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    {[
                      "Un rapport de 200 pages que personne ne lit",
                      "Un audit technique sans plan d'action",
                      "Des recommandations théoriques déconnectées du terrain",
                      "Un outil de plus à gérer sans accompagnement"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                          <X className="h-5 w-5 text-red-600" />
                        </div>
                        <span className="text-gray-700 pt-1 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-green-500 bg-green-50">
                <CardHeader>
                  <CardTitle className="text-2xl text-green-700 flex items-center gap-2">
                    <CheckCircle2 className="h-6 w-6" />
                    CE QUE VOUS OBTENEZ RÉELLEMENT
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    {[
                      "Un système de conformité opérationnel et documenté",
                      "Des livrables directement utilisables en audit",
                      "Un accompagnement adapté à votre organisation",
                      "Une gouvernance claire avec la direction impliquée"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        </div>
                        <span className="text-gray-800 pt-1 leading-relaxed font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Process Timeline */}
        <div className="corporate-section-alt">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center mb-12">
              
              <h2 className="corporate-heading text-4xl md:text-5xl mb-4">
                Un processus clair, des résultats mesurables
              </h2>
              <p className="text-xl text-gray-600">
                De l'éligibilité à l'audit, en 10 à 15 semaines.
              </p>
            </div>

            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                  {
                    number: "01",
                    title: "Audit d'éligibilité",
                    description: "Analyse de votre périmètre et identification de vos obligations NIS2.",
                    duration: "1 semaine"
                  },
                  {
                    number: "02",
                    title: "Diagnostic & feuille de route",
                    description: "Évaluation de votre maturité et définition du plan de mise en conformité.",
                    duration: "2 semaines"
                  },
                  {
                    number: "03",
                    title: "Déploiement opérationnel",
                    description: "Construction du système de conformité : politiques, processus, documentation.",
                    duration: "6–10 semaines"
                  },
                  {
                    number: "04",
                    title: "Validation & audit blanc",
                    description: "Test du dispositif et préparation du dossier d'audit final.",
                    duration: "2 semaines"
                  }
                ].map((step, index) => (
                  <div key={index} className="relative">
                    <div className="text-center">
                      <div className="stat-number text-blue-600 mb-4">
                        {step.number}
                      </div>
                      
                      <div className="corporate-card">
                        <h3 className="text-lg font-bold text-blue-900 mb-3">{step.title}</h3>
                        <p className="text-gray-600 mb-4 text-sm leading-relaxed">{step.description}</p>
                        <div className="inline-block px-3 py-1 bg-blue-50 rounded-full">
                          <p className="text-sm font-semibold text-blue-700">⏰ {step.duration}</p>
                        </div>
                      </div>
                    </div>
                    
                    {index < 3 && (
                      <div className="hidden md:block absolute top-8 left-[calc(50%+2rem)] w-[calc(100%-4rem)] z-0">
                        <div className="h-0.5 bg-gray-300">
                          <ArrowRight className="absolute right-0 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION SERVICES - NOS 3 PILIERS */}
      <section id="services" className="scroll-mt-20">
        <div className="corporate-section">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="corporate-heading text-4xl md:text-5xl mb-4">
                Nos services
              </h2>
              <p className="text-xl text-gray-600">
                Trois piliers pour sécuriser votre entreprise de manière pragmatique.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {/* Carte 1 - Conformité réglementaire */}
              <Card className="corporate-card hover:shadow-xl transition-shadow">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 rounded-lg bg-blue-600 flex items-center justify-center mb-4">
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-blue-900">Conformité réglementaire</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">
                    Nous vous aidons à <strong>anticiper et maîtriser vos obligations cyber</strong> avant qu'elles ne vous rattrapent.
                  </p>
                </CardContent>
              </Card>

              {/* Carte 2 - AI Risk Assessment */}
              <Card className="corporate-card border-2 border-blue-600 shadow-xl relative hover:shadow-2xl transition-shadow">
                <div className="absolute -top-3 right-4 z-10">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-1 rounded-full shadow-lg text-xs font-bold flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    Nouveauté
                  </div>
                </div>
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center mb-4">
                    <BarChart3 className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-blue-900">AI Risk Assessment</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">
                    Notre solution d'<strong>AI-driven Risk Assessment</strong> analyse, quantifie et monitore vos risques cyber en continu grâce à l'intelligence artificielle.
                  </p>
                </CardContent>
              </Card>

              {/* Carte 3 - Accompagnement stratégique */}
              <Card className="corporate-card hover:shadow-xl transition-shadow">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 rounded-lg bg-blue-600 flex items-center justify-center mb-4">
                    <TrendingUp className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-blue-900">Accompagnement stratégique</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">
                    De l'audit à la mise en œuvre, <strong>nous restons à vos côtés</strong> pour transformer vos contraintes en force.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* SECTION POURQUOI SECURA ? */}
        <div className="corporate-section-alt">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="corporate-heading text-4xl md:text-5xl mb-4">
                Pourquoi Secura ?
              </h2>
              <p className="text-xl text-gray-600">
                Ce qui nous différencie des autres acteurs du marché.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Argument 1 */}
              <Card className="corporate-card hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Lightbulb className="h-6 w-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl text-blue-900">Pas de jargon inutile</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">
                    Nous parlons le langage de votre métier, pas celui des auditeurs.
                  </p>
                </CardContent>
              </Card>

              {/* Argument 2 */}
              <Card className="corporate-card hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl text-blue-900">Taille humaine, expertise senior</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">
                    Vous travaillez directement avec la fondatrice, pas avec des juniors.
                  </p>
                </CardContent>
              </Card>

              {/* Argument 3 */}
              <Card className="corporate-card hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Sparkles className="h-6 w-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl text-blue-900">IA au service de votre sécurité</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">
                    Notre solution AI Risk Assessment vous donne une vision claire et continue de vos risques.
                  </p>
                </CardContent>
              </Card>

              {/* Argument 4 */}
              <Card className="corporate-card hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Target className="h-6 w-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl text-blue-900">Résultats concrets</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">
                    Notre objectif : que vous soyez réellement sécurisés, pas juste conformes sur le papier.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* SECTION TÉMOIGNAGES */}
        <div className="corporate-section">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="corporate-heading text-4xl md:text-5xl mb-8">
                Témoignages clients
              </h2>
              <div className="bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300 p-12">
                <p className="text-xl text-gray-500 italic">
                  Nos premiers clients partagent bientôt leur expérience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION FAQ */}
      <section id="faq" className="scroll-mt-20 corporate-section-alt">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="corporate-heading text-4xl md:text-5xl mb-4">
              Questions fréquentes
            </h2>
            <p className="text-xl text-gray-600">
              Tout ce que vous devez savoir sur la conformité NIS2
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {[
                {
                  question: "Mon entreprise est-elle concernée par NIS2 ?",
                  answer: "La directive NIS2 s'applique aux entreprises des secteurs critiques (énergie, transport, santé, finance, etc.) ainsi qu'aux fournisseurs de services numériques. Si votre entreprise compte plus de 50 employés ou réalise un chiffre d'affaires supérieur à 10M€ dans ces secteurs, vous êtes probablement concerné. Notre audit d'éligibilité vous donnera une réponse précise."
                },
                {
                  question: "Combien de temps faut-il pour devenir conforme ?",
                  answer: "Le processus complet prend entre 10 et 15 semaines selon votre maturité initiale et le niveau d'accompagnement choisi. Nous déployons une approche structurée en 4 phases : audit d'éligibilité (1 semaine), diagnostic (2 semaines), déploiement opérationnel (6-10 semaines), et validation (2 semaines)."
                },
                {
                  question: "Faut-il une équipe cybersécurité en interne ?",
                  answer: "Non, ce n'est pas obligatoire. Notre accompagnement est conçu pour s'adapter à votre structure actuelle. Nous travaillons avec votre équipe existante et formons vos collaborateurs aux processus de conformité. L'important est d'avoir un système opérationnel et auditable, pas nécessairement une équipe dédiée."
                },
                {
                  question: "Quelle différence avec un audit de cybersécurité classique ?",
                  answer: "Un audit de cybersécurité évalue vos vulnérabilités techniques. La conformité NIS2 va au-delà : elle exige un système de gouvernance, des processus documentés, une traçabilité complète et l'engagement de la direction. Nous construisons ce système opérationnel, pas seulement un rapport d'audit."
                },
                {
                  question: "Les livrables sont-ils réutilisables en cas d'audit ?",
                  answer: "Absolument. Tous nos livrables sont conçus pour être directement présentables lors d'un audit officiel. Ils respectent les exigences réglementaires, sont structurés selon les standards reconnus, et incluent toute la documentation et la traçabilité nécessaires pour démontrer votre conformité."
                }
              ].map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  className="corporate-card"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-blue-900 hover:text-blue-700 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-700 font-bold text-sm">Q{index + 1}</span>
                      </div>
                      <span>{item.question}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed pl-11 pr-4 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="corporate-section">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="corporate-heading text-4xl md:text-5xl mb-4">
              Ils nous font confiance
            </h2>
            <p className="text-xl text-gray-600">
              PME et ETI françaises accompagnées vers la conformité NIS2
            </p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-16 max-w-5xl mx-auto">
            <div className="corporate-card hover:shadow-xl transition-shadow p-8 rounded-lg">
              <img 
                src="/logos/societe-generale.svg" 
                alt="Société Générale"
                className="h-16 w-auto"
              />
            </div>

            <div className="corporate-card hover:shadow-xl transition-shadow p-8 rounded-lg">
              <img 
                src="/logos/bnp-paribas.svg" 
                alt="BNP Paribas"
                className="h-16 w-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION CONTACT */}
      <section id="contact" className="scroll-mt-20 py-12 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-3 text-white">
                Êtes-vous prêt à démontrer votre conformité NIS2 ?
              </h2>
              <p className="text-lg text-blue-100 max-w-2xl mx-auto">
                Obtenez un diagnostic clair et une feuille de route actionnable.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Form */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h3 className="text-xl font-bold mb-5 text-white">Demander un audit</h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white font-medium">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-white border-white/30 text-gray-900"
                      placeholder="Jean Dupont"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white font-medium">Email professionnel *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-white border-white/30 text-gray-900"
                      placeholder="jean.dupont@entreprise.fr"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-white font-medium">Entreprise *</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="bg-white border-white/30 text-gray-900"
                      placeholder="Nom de votre entreprise"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-white font-medium">Téléphone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-white border-white/30 text-gray-900"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white font-medium">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="bg-white border-white/30 text-gray-900 resize-none"
                      placeholder="Décrivez brièvement vos besoins..."
                    />
                  </div>

                  <Button 
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-white text-blue-900 hover:bg-blue-50 shadow-lg"
                  >
                    <Send className="mr-2 h-5 w-5" />
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                  </Button>

                  {submitMessage && (
                    <p className={`text-sm text-center ${submitMessage.includes('Merci') ? 'text-green-300' : 'text-red-300'}`}>
                      {submitMessage}
                    </p>
                  )}

                  <p className="text-sm text-blue-100 text-center">
                    Réponse sous 24h ouvrées
                  </p>
                </form>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h3 className="text-lg font-bold mb-4 text-white">Informations de contact</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-4">
                      <Mail className="h-6 w-6 text-blue-300 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-blue-200">Email</p>
                        <p className="text-white font-medium">contact@secura-consulting.fr</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Les obligations cyber arrivent. Soyez prêts avant tout le monde.
            </h2>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
              Parlons de votre situation. Premier échange offert.
            </p>
            <Button 
              size="lg"
              onClick={() => scrollToSection('contact')}
              className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-10 py-6 shadow-xl"
            >
              Prendre RDV
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-10 border-t border-gray-800">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/assets/secura-logo.png" 
                alt="SECURA Consulting" 
                className="h-12 w-auto opacity-90"
                style={{ mixBlendMode: 'lighten', objectFit: 'contain' }}
              />
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="hover:text-white transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </div>
            
            <p className="text-sm text-center">© 2026 SECURA Consulting. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;


